import debounce from 'lodash-es/debounce.js';
import { ApplicationElement, html } from '@crossiety/lit-components';
import CommonActions from '@crossiety/lit-components/components/common_actions/element.js';
import '@crossiety/lit-components/components/avatar_list/element.js';
import '@crossiety/lit-components/components/label/element.js';
import '@crossiety/lit-components/components/icon/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * One card option - This component is extended by other card options and should not be used on its own
 *
 * @prop itemId - Id of the item
 * @prop cardId - Id of the card
 * @prop engagedUsersCount - Total number of users who clicked for this option
 * @prop checked - Did current user vote? (optional)
 * @prop unique - Should this option act as a radio option? If true, checking this option will uncheck all other options wrapped in the same `<xs-card-option-wrapper>` and vice versa (optional)
 * @prop disabled - Should disable action? (optional)
 * @prop layout - Layout of item (options: row, column)
 * @prop resultsHidden - Hide the results of voting?
 * @prop skeleton - Should show component skeleton (loading state)? Title can be passed via default slot.
 * @prop readOnlyOn - Option is read only? The action gets hidden, and if the option is checked, the config.icons.check icon is shown (set to "" if it should be hidden too).
 *
 * @cssprop [--count-color: var(--dark-text-secondary)] Count label text color
 * @cssprop [--count-font-size: var(--font-size-sm)] Count label text size
 * @cssprop [--count-padding: 4px] Count label padding
 * @cssprop [--count-margin: 0 16px 0 0] Count label margin
 * @cssprop [--count-background: var(--contrast-light)] Count label background
 * @cssprop [--count-label-display: none] Display of count "Helpers" label (recommended: none | inline)
 * @cssprop [--button-color: var(--dark-text)] Action button text color--count-color: var(--dark-text-secondary);
 * @cssprop [--button-color-hover: var(--primary)] Action button text color on hover
 * @cssprop [--button-color-active: var(--dark-text-secondary)] Action button text color on active
 * @cssprop [--button-margin: 0 0 0 16px] Action button margin
 * @cssprop [--button-padding: 0] Action button padding
 * @cssprop [--button-icon-size: 24px] Action button text icon size
 * @cssprop [--button-border: none] Action button's border
 * @cssprop [--button-border-radius: 0] Action button's border radius
 * @cssprop [--button-size: auto] Width and height of action button
 *
 * @slot default - Title/label
 * @slot avatars - Avatar list (recommended: ef-avatar-list)
 */

export default class CardOption extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    itemId: { type: Number },
    cardId: { type: Number },
    engagedUsersCount: { type: Number, reflect: true },
    checked: { type: Boolean, reflect: true },
    unique: { type: Boolean },
    resultsHidden: { type: Boolean, reflect: true },
    skeleton: { type: Boolean },
    readOnlyOn: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    layout: { type: String, reflect: true },
    users: { type: Array, attribute: false },
    preventInput: { type: Boolean, state: true },
    maxAvatarsLength: { type: Number, state: true },
  };

  /** @internal */
  static config = {
    icons: {
      plus: 'choose-icon-plus',
      minus: 'choose-icon-minus',
      check: 'choose-icon-check',
    },
  };

  constructor() {
    super();
    this.engagedUsersCount = 0;
    this.layout = 'column';
    this.users = [];
    this.maxAvatarsLength = 5;
    /** @internal */
    this.commonActions = new CommonActions(this);
    /** @internal */
    this.debouncedHandleResize = debounce(() => this.handleResize(), 50);
  }

  connectedCallback() {
    super.connectedCallback();

    this.initLayout = this.layout;
    this.handleResize();
    window.addEventListener('resize', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('users') && this.avatarList && this.users) {
      const newUsers = this.users.filter(u => u.id !== this.currentUser.id); // we will return currentUser - at the end, as it should always be on top
      if (this.checked) newUsers.push(this.currentUser);

      this.avatarList.users = newUsers.slice(-this.maxAvatarsLength);
    }

    if (changedProperties.has('engagedUsersCount') && this.avatarList) {
      this.avatarList.totalCount = this.engagedUsersCount || 0;
    }

    if (changedProperties.has('skeleton') && this.skeleton) {
      this.disabled = true;
    }
  }

  /** @internal */
  get icon() {
    return this.constructor.config.icons[this.checked ? 'minus' : 'plus'];
  }

  /** @internal */
  get checkUrl() {
    return `/implement-check-url-in-subclass`;
  }

  /** @internal */
  get uncheckUrl() {
    return `/implement-uncheck-url-in-subclass`;
  }

  /** @internal */
  get detailsUrl() {
    return `/implement-details-url-in-subclass`;
  }

  /** @internal */
  get currentUser() {
    const { user } = this.AppConfig;
    return { id: user?.id, picture: user?.avatar, name: user?.fullName };
  }

  handleEvent(e) {
    e.type === 'resize' && this.debouncedHandleResize();
  }

  handleResize() {
    if (window.innerWidth < 768) {
      this.layout = 'row';
    } else {
      this.layout = this.initLayout;
    }
  }

  dispatchChangeEvent() {
    this.dispatchEvent(
      new Event('cardOption-change', {
        bubbles: true,
        composed: true,
      })
    );
  }

  checkAction() {
    return this.commonActions.post(this.checkUrl);
  }

  // We separate calculations from side-effects (event, snack, request...) because they will be used often independently
  calcCheck() {
    if (this.checked) return;

    this.checked = true;
    this.engagedUsersCount++;

    if (!this.users) return;

    this.users = [...this.users, this.currentUser];
  }

  check() {
    this.calcCheck();
    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.check_success'));
  }

  uncheckAction() {
    this.commonActions.delete(this.uncheckUrl);
  }

  calcUncheck() {
    if (!this.checked) return;

    this.checked = false;
    this.engagedUsersCount--;

    if (!this.users) return;

    this.users = this.users.filter(a => a.id !== this.currentUser.id);
  }

  uncheck() {
    this.calcUncheck();
    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.uncheck_success'));
  }

  handleClick() {
    if (this.preventInput || this.disabled || this.readOnlyOn) return;

    this.preventInput = true;

    try {
      if (!this.checked) {
        this.check();
        this.checkAction();
      } else if (this.checked) {
        this.uncheck();
        this.uncheckAction();
      }
    } catch {
      this.checked ? this.uncheck() : this.check();
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    } finally {
      this.preventInput = false;
    }
  }

  handleAvatarSlotChange(e) {
    this.avatarList = e.target
      .assignedElements({ flatten: true })
      .find(e => e.tagName === 'EF-AVATAR-LIST');

    if (!this.avatarList) return;

    this.avatarList.url ||= this.detailsUrl;
    this.avatarList.totalCount ||= this.engagedUsersCount || 0;
    this.users = this.avatarList.users || [];
  }

  renderLabel() {
    return html`
      <ef-label>
        ${this.engagedUsersCount}
        <span class="count-label">
          ${this.t('count_label', {
            count: this.engagedUsersCount,
          })}
        </span>
      </ef-label>
    `;
  }

  renderAction() {
    return html`
      <div class="action-wrapper">
        <button
          @click=${this.handleClick}
          aria-label=${this.t(this.checked ? 'remove_vote' : 'add_vote')}>
          <ef-icon .name=${this.icon}></ef-icon>
        </button>
      </div>
    `;
  }

  renderCheckedIcon() {
    return html`
      <div class="action-wrapper">
        <ef-icon name=${this.constructor.config.icons.check}></ef-icon>
      </div>
    `;
  }

  renderActionWrapper() {
    return html`
      ${!this.readOnlyOn ? this.renderAction() : null}
      ${this.readOnlyOn && this.checked ? this.renderCheckedIcon() : null}
    `;
  }

  renderAvatars() {
    return html`
      <slot @slotchange=${this.handleAvatarSlotChange} name="avatars"></slot>
    `;
  }

  renderTitle() {
    return html`<slot> ${this.skeleton ? this.t('loading') : null} </slot>`;
  }

  renderColumnLayout() {
    return html`
      <div class="left">
        ${this.renderTitle()} ${this.resultsHidden ? null : this.renderLabel()}
      </div>
      <div class="right">
        ${this.renderAvatars()} ${this.renderActionWrapper()}
      </div>
    `;
  }

  renderRowLayout() {
    return html`
      <div class="top">${this.renderTitle()}</div>
      <div class="bottom">
        <div class="left">
          ${this.resultsHidden ? null : this.renderLabel()}
          ${this.renderAvatars()}
        </div>
        <div class="right">${this.renderActionWrapper()}</div>
      </div>
    `;
  }

  render() {
    return this.layout === 'row'
      ? this.renderRowLayout()
      : this.renderColumnLayout();
  }
}

CardOption.registerTagName('xs-card-option');

customElements.get('xs-card-option') ||
  customElements.define('xs-card-option', CardOption);
